"use client";

import { useState } from "react";

// Same localStorage key src/lib/analytics/track.ts uses for its anon ID --
// reusing it (read-only here) lets an admin correlate a feedback message
// with that visitor's analytics trail without introducing a second ID.
const ANON_ID_KEY = "nohaya-anon-id";

function getAnonId(): string | null {
  try {
    return localStorage.getItem(ANON_ID_KEY);
  } catch {
    return null;
  }
}

export default function FeedbackBubble() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");

  function close() {
    setOpen(false);
    setStatus("idle");
    setEmail("");
    setMessage("");
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!message.trim() || status === "sending") return;
    setStatus("sending");
    try {
      const res = await fetch("/api/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: email.trim() || undefined,
          message: message.trim(),
          pagePath: window.location.pathname,
          anonId: getAnonId() || undefined,
        }),
      });
      if (!res.ok) throw new Error("request failed");
      setStatus("sent");
      setTimeout(close, 2000);
    } catch {
      setStatus("error");
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Send feedback"
        className="fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-violet-600 to-fuchsia-600 text-white shadow-lg shadow-violet-500/30 transition hover:scale-105 hover:brightness-110"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-6 w-6">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8-1.135 0-2.222-.183-3.223-.52L3 21l1.395-3.72C3.512 16.11 3 14.607 3 13c0-4.418 4.03-8 9-8s9 3.582 9 7z"
          />
        </svg>
      </button>

      {open ? (
        <div className="fixed bottom-24 right-6 z-50 w-[calc(100vw-3rem)] max-w-sm rounded-2xl border border-slate-200 bg-white p-5 shadow-2xl dark:border-slate-800 dark:bg-[#0f172a]">
          <div className="flex items-start justify-between">
            <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Send us feedback</h3>
            <button
              type="button"
              onClick={close}
              aria-label="Close"
              className="-mr-1 -mt-1 flex h-7 w-7 items-center justify-center rounded-full text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 dark:text-slate-500 dark:hover:bg-slate-800 dark:hover:text-slate-300"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-4 w-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M6 18L18 6" />
              </svg>
            </button>
          </div>

          {status === "sent" ? (
            <p className="mt-4 text-sm text-slate-600 dark:text-slate-400">Thanks — got it! 🙌</p>
          ) : (
            <form className="mt-3 space-y-3" onSubmit={handleSubmit}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email (optional)"
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-violet-400 focus:outline-none dark:border-slate-700 dark:bg-[#0b1220] dark:text-slate-200"
              />
              <textarea
                required
                autoFocus
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="What's on your mind?"
                rows={4}
                className="w-full resize-none rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 focus:border-violet-400 focus:outline-none dark:border-slate-700 dark:bg-[#0b1220] dark:text-slate-200"
              />
              {status === "error" ? (
                <p className="text-xs text-rose-500">Something went wrong — please try again.</p>
              ) : null}
              <button
                type="submit"
                disabled={status === "sending" || !message.trim()}
                className="w-full rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:opacity-50 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200"
              >
                {status === "sending" ? "Sending…" : "Send"}
              </button>
            </form>
          )}
        </div>
      ) : null}
    </>
  );
}
